import {
  DELETE,
  GET,
  POST,
  PUT,
  UPLOAD_FILE,
  UPLOAD_FILE_WITH_METHOD,
} from "../../apiController";

const endpoint = "/admin/courses";

// ---------------------------------- Courses Content APIs

// get course module
export const getCourseModule = (data) => {
  return GET(endpoint + `/modules/${data.id}?search=${data.search || ""}`);
};

export const verifyAdminCourseAccess = (data) => {
  return POST(endpoint + `/verify_course_access`, data);
};

// create module
export const createModule = (data) => {
  return POST(endpoint + `/module/new`, data);
};

// delete module
export const deleteModule = async (data) => {
  return DELETE(
    endpoint + `/course/${data.course_id}/module/${data.module_id}`
  );
};

// update module
export const updateModule = (moduleId, data) => {
  return PUT(endpoint + `/module/${moduleId}`, data);
};

// update module status
export const moduleStatus = (data) => {
  return PUT(endpoint + `/course/${data.course_id}/module/${data.module_id}`, {
    status: data.status,
  });
};

export const getVideoDetailsById = (data) => {
  return GET(
    endpoint + `/get_video_details/${data.video_id}/${data.module_id}`
  );
};

export const getCourseReviews = (course_id, video_id) => {
  const queryParams = video_id
    ? `course_id=${course_id}&video_id=${video_id}`
    : `course_id=${course_id}`;
  return GET(`/org/get_course_reviews?${queryParams}`);
};

// get_document_data/:doc_id — document_name, earning_points, document_url (signed)
export const getDocumentData = (data) => {
  return GET(endpoint + `/get_document_data/${data.doc_id}`);
};

// get module questions
export const getModuleQuestions = (data) => {
  return GET(
    endpoint + `/course/get-questions/${data.module_id}/${data.assessment_id}`
  );
};

export const getCourseVideos = (data) => {
  const moduleParam = data.module_id ? `/${data.module_id}` : "";
  return GET(endpoint + `/course/videos/${data.course_id}${moduleParam}`);
};

export const addQuestion = async (data) => {
  try {
    // If data is FormData, use it directly; otherwise use POST
    let response;
    if (data instanceof FormData) {
      response = await UPLOAD_FILE(endpoint + `/course/add-questions`, data);
    } else {
      response = await POST(endpoint + `/course/add-questions`, data);
    }

    // If response is undefined, create a default error response
    if (!response) {
      return {
        success: true,
        message: "Question may have been added, but no response was received",
      };
    }

    // If we have a response with success property, it's already formatted correctly
    if (response.success !== undefined) {
      return response;
    }

    return {
      success: true,
      message: "Question added successfully",
      data: response,
    };
  } catch (error) {
    return { success: false, error: error.toString() };
  }
};

export const deleteQuestion = (data) => {
  return DELETE(endpoint + `/question/${data.question_id}`);
};

export const editQuestion = (data) => {
  // If data is FormData, use it directly with PUT method; otherwise use PUT
  if (data instanceof FormData) {
    const questionId = data.get("id");
    return UPLOAD_FILE_WITH_METHOD(
      endpoint + `/course/edit-question/${questionId}`,
      data,
      "PUT"
    );
  } else {
    const questionId = data.id;
    return PUT(endpoint + `/course/edit-question/${questionId}`, data);
  }
};

// ==================== CLASSROOM ASSIGNMENT QUIZ QUESTIONS ====================

// Get all questions for a specific classroom assignment quiz
export const getAssignmentQuizQuestions = (data) => {
  return GET(
    endpoint + `/classroom/get_quiz_questions?assignment_id=${data.assignment_id}&assignment_quiz_id=${data.assignment_quiz_id}`
  );
};

// Add a question to a classroom assignment quiz
export const addAssignmentQuizQuestion = async (data) => {
  try {
    let response;
    if (data instanceof FormData) {
      response = await UPLOAD_FILE(endpoint + `/classroom/add_quiz_question`, data);
    } else {
      response = await POST(endpoint + `/classroom/add_quiz_question`, data);
    }

    if (!response) {
      return {
        success: true,
        message: "Question may have been added, but no response was received",
      };
    }

    if (response.success !== undefined) {
      return response;
    }

    return {
      success: true,
      message: "Question added successfully",
      data: response,
    };
  } catch (error) {
    return { success: false, error: error.toString() };
  }
};

// Edit a question in a classroom assignment quiz
export const editAssignmentQuizQuestion = (data) => {
  if (data instanceof FormData) {
    return UPLOAD_FILE_WITH_METHOD(
      endpoint + `/classroom/edit_quiz_question`,
      data,
      "PUT"
    );
  } else {
    return PUT(endpoint + `/classroom/edit_quiz_question`, data);
  }
};

// Delete a question from a classroom assignment quiz
export const deleteAssignmentQuizQuestion = (data) => {
  return DELETE(endpoint + `/classroom/delete_quiz_question`, data);
};

// Get detailed quiz results for a specific user in a classroom assignment quiz
export const getAssignmentQuizResultsDetailed = (params) => {
  const { assignment_id, quiz_id, user_id } = params;

  return GET(
    endpoint + `/classroom/get_quiz_results_detailed?assignment_id=${assignment_id}&quiz_id=${quiz_id}&user_id=${user_id}`
  );
};



// --- below this. course content apis ---

// ==================== USER COURSE CONTENT APIS ====================

const USER = "/user";
const ORG_USER_COURSE = `${USER}/usercourse`;
const ORG_USER_PROFILE = '/user/userprofile';

// ---------------------------------------------------------------------------
// Comments APIs
// ---------------------------------------------------------------------------

export const getComments = (video_id) => {
  return GET(`${ORG_USER_COURSE}/get_comments_details?video_id=${video_id}`);
};

export const addComment = (data) => {
  return POST(`${ORG_USER_COURSE}/add_comment`, data);
};

export const updateComment = (data) => {
  return POST(`${ORG_USER_COURSE}/update_comment`, data);
};

export const deleteComment = (comment_id) => {
  return DELETE(`${ORG_USER_COURSE}/delete_comment/${comment_id}`);
};

export const updateLike = (commentId) => {
  return POST(`${ORG_USER_COURSE}/update_like`, { commentId });
};

export const addReplyComment = (data) => {
  return POST(`${ORG_USER_COURSE}/add_reply_comment`, data);
};

export const getRepliesByCommentId = (data) => {
  return POST(`${ORG_USER_COURSE}/get_replies_by_comment_id`, data);
};

export const updateReplyComment = (data) => {
  return POST(`${ORG_USER_COURSE}/update_reply_comment`, data);
};

export const deleteReplyComment = (reply_id) => {
  return POST(`${ORG_USER_COURSE}/delete_reply_comment`, { reply_id });
};

// ---------------------------------------------------------------------------
// User Profile APIs
// ---------------------------------------------------------------------------

export const getProfileComments = (user_id) => {
  return GET(`${ORG_USER_PROFILE}/get_profile_comments/${user_id}`);
};